import React, { useEffect, useState } from "react";
import api from "../services/api";
import NieuweKlantModal from "../components/NieuweKlantModal";
import { useUser } from "../context/UserContext";
import { genereerWerkbonNummer } from "../services/werkbonNummerService";
import { uploadWorkorderAttachments } from "../services/workorderDocumentService";
import { parsePositiveWholeMinutes } from "../utils/plannedMinutes";
import {
  getApiErrorMessage,
  getApiFieldErrors,
  hasApiErrorCode,
  showApiErrorToast,
} from "../utils/apiError";

export default function NieuweWerkbonModal({
  onClose,
  onCreated,
  initialClientId = "",
  initialProjectId = "",
}) {
  const { user } = useUser();
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [showNewClient, setShowNewClient] = useState(false);

  const [type, setType] = useState("service");
  const [omschrijving, setOmschrijving] = useState("");
  const [locatie, setLocatie] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [plannedMinutesInput, setPlannedMinutesInput] = useState("120");
  const [bijlageFiles, setBijlageFiles] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [installationId, setInstallationId] = useState("");
  const [installationAssets, setInstallationAssets] = useState([]);
  const [assetId, setAssetId] = useState("");
  const [assetLogbookEnabled, setAssetLogbookEnabled] = useState(false);
  const [assetLogbookCategory, setAssetLogbookCategory] = useState("");
  const [assetLogbookCustomerVisible, setAssetLogbookCustomerVisible] = useState(false);
  const [assetLogbookTouched, setAssetLogbookTouched] = useState(false);

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [monteurs, setMonteurs] = useState([]);
  const [workorderTypeOptions, setWorkorderTypeOptions] = useState(LEGACY_TYPE_OPTIONS);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitMode, setSubmitMode] = useState("concept");
  const [autoLocation, setAutoLocation] = useState("");
  const [zzpMode, setZzpMode] = useState(false);

  const [werkbonNummer] = useState(() => genereerWerkbonNummer());
  const roleNames = getRoleNames(user);
  const isMonteur = roleNames.includes("monteur");
  const currentUserId = String(user?.id || user?.userId || user?._id || "").trim();

  useEffect(() => {
    async function load() {
      try {
        const createOptionsRequest = api.get("/werkbonnen/create-options");
        const monteursRequest = isMonteur ? Promise.resolve({ data: [] }) : api.get("/users?role=Monteur");
        const tenantSettingsRequest = isMonteur ? Promise.resolve({ data: {} }) : api.get("/tenant/settings");
        const clientsRequest = isMonteur ? Promise.resolve({ data: [] }) : api.get("/clients");
        const projectsRequest = isMonteur ? Promise.resolve({ data: [] }) : api.get("/projects");

        const [createOptionsRes, monteursRes, tenantSettingsRes, clientsRes, projectsRes] =
          await Promise.all([
            createOptionsRequest,
            monteursRequest,
            tenantSettingsRequest,
            clientsRequest,
            projectsRequest,
          ]);

        const nextClients = isMonteur
          ? (Array.isArray(createOptionsRes?.data?.clients) ? createOptionsRes.data.clients : [])
          : (Array.isArray(clientsRes?.data) ? clientsRes.data : []);
        const nextProjects = isMonteur
          ? (Array.isArray(createOptionsRes?.data?.projects) ? createOptionsRes.data.projects : [])
          : (Array.isArray(projectsRes?.data) ? projectsRes.data : []);
        const zzpModeEnabled = createOptionsRes?.data?.zzpMode === true;
        setZzpMode(zzpModeEnabled);
        setClients(nextClients);
        setProjects(nextProjects);
        const fetchedMonteurs = asArray(monteursRes.data);
        if (fetchedMonteurs.length > 0) {
          setMonteurs(fetchedMonteurs);
        } else if (isMonteur && currentUserId) {
          setMonteurs([
            {
              _id: currentUserId,
              naam: user?.naam || user?.name || "Monteur",
              email: user?.email || "",
            },
          ]);
          setAssignedTo(currentUserId);
        } else {
          setMonteurs([]);
        }
        const tenantTypeOptions = normalizeWorkorderTypeOptions(
          tenantSettingsRes?.data?.workorderTypes
        );
        const resolvedTypeOptions = tenantTypeOptions.length > 0 ? tenantTypeOptions : LEGACY_TYPE_OPTIONS;
        setWorkorderTypeOptions(resolvedTypeOptions);
        const visibleDefault =
          tenantSettingsRes?.data?.modules?.fireSafetyLogbook?.assetLogbookCustomerVisibleDefault === true;
        setAssetLogbookCustomerVisible(visibleDefault);
        setType((prev) => {
          const prevKey = String(prev || "").trim().toLowerCase();
          if (resolvedTypeOptions.some((item) => item.value === prevKey)) return prevKey;
          return String(resolvedTypeOptions[0]?.value || LEGACY_TYPE_OPTIONS[0].value);
        });
        setInstallations(Array.isArray(createOptionsRes?.data?.installations) ? createOptionsRes.data.installations : []);

        if (initialClientId) {
          setClientId(String(initialClientId));
        } else if (isMonteur && zzpModeEnabled && nextClients.length === 1) {
          setClientId(String(nextClients[0]._id || ""));
        } else if (nextClients.length === 1) {
          setClientId(String(nextClients[0]._id));
        }

        if (initialProjectId) {
          setProjectId(String(initialProjectId));
        } else if (isMonteur && nextProjects.length === 1) {
          setProjectId(String(nextProjects[0]._id || ""));
          setClientId(String(nextProjects[0]?.clientId || ""));
        }
      } catch (err) {
        console.error("Werkbon modal laden mislukt:", err);
        setError(getApiErrorMessage(err, "Basisgegevens voor werkbon aanmaken konden niet worden geladen."));
      }
    }

    load();
  }, [currentUserId, initialClientId, initialProjectId, isMonteur, user?.email, user?.name, user?.naam]);

  useEffect(() => {
    if (assetLogbookTouched) return;
    const selectedTypeOption =
      workorderTypeOptions.find((option) => option.value === type) || LEGACY_TYPE_OPTIONS[0];
    const shouldEnable = shouldDefaultAssetLogbookEnabled(selectedTypeOption);
    setAssetLogbookEnabled(shouldEnable);
    if (shouldEnable && !assetLogbookCategory) {
      setAssetLogbookCategory(defaultAssetLogbookCategory(selectedTypeOption));
    }
  }, [assetLogbookCategory, assetLogbookTouched, type, workorderTypeOptions]);

  useEffect(() => {
    let active = true;
    async function loadAssets() {
      if (!installationId) {
        setInstallationAssets([]);
        setAssetId("");
        return;
      }
      try {
        const res = await api.get(`/fire-logbook/installations/${installationId}`);
        if (!active) return;
        const assets = Array.isArray(res?.data?.assets) ? res.data.assets : [];
        setInstallationAssets(assets);
        if (!assets.some((item) => String(item?._id || "") === String(assetId))) {
          setAssetId("");
        }
      } catch {
        if (!active) return;
        setInstallationAssets([]);
        setAssetId("");
      }
    }
    loadAssets();
    return () => {
      active = false;
    };
  }, [assetId, installationId]);

  useEffect(() => {
    if (isMonteur && !zzpMode) return;
    if (!clientId) {
      if (!isMonteur || !zzpMode) {
        setProjectId("");
      }
      return;
    }

    const matchesSelectedClient = projects.some(
      (project) =>
        String(project?.clientId?._id || project?.clientId || "") === String(clientId) &&
        String(project?._id || "") === String(projectId)
    );

    if (!matchesSelectedClient) {
      setProjectId("");
    }
  }, [clientId, isMonteur, projectId, projects, zzpMode]);

  const selectedClient = clients.find((client) => String(client?._id || "") === String(clientId));
  const selectedProject = projects.find((project) => String(project?._id || "") === String(projectId));
  const selectedTypeOptionForForm =
    workorderTypeOptions.find((option) => option.value === type) || LEGACY_TYPE_OPTIONS[0];
  const selectedProjectClientId = resolveClientIdFromProject(selectedProject);
  const filteredInstallations = installations.filter((item) => {
    const itemClientId = String(item?.customerId || "").trim();
    const effectiveClientId = String(selectedProjectClientId || clientId || "").trim();
    if (!effectiveClientId) return true;
    return itemClientId && itemClientId === effectiveClientId;
  });
  const selectedContact = getSuggestedContact(selectedClient, selectedProject);
  const locationSourceLabel = selectedProject?.locationText ? "Voorgesteld vanuit project" : "";
  const contactSourceLabel = selectedProject?.contactPersonId
    ? "Voorgesteld vanuit project"
    : selectedContact
      ? "Voorgesteld vanuit klant"
      : "";

  useEffect(() => {
    if (!isMonteur) return;
    const projectClientId = selectedProjectClientId;
    if (projectClientId && projectClientId !== String(clientId || "")) {
      setClientId(projectClientId);
    }
  }, [clientId, isMonteur, selectedProjectClientId]);

  useEffect(() => {
    const nextAutoLocation = String(selectedProject?.locationText || "").trim();

    if (!nextAutoLocation) {
      if (locatie === autoLocation) {
        setLocatie("");
      }
      setAutoLocation("");
      return;
    }

    if (!locatie || locatie === autoLocation) {
      setLocatie(nextAutoLocation);
    }

    setAutoLocation(nextAutoLocation);
  }, [autoLocation, locatie, selectedProject]);

  async function handleSubmit(event) {
    event.preventDefault();
    setLoading(true);
    setError("");
    setFieldErrors({});

    try {
      const wantsDirectPlanning = submitMode === "direct";
      if (isMonteur && !zzpMode && !String(projectId || "").trim()) {
        setError("Kies een bestaand project.");
        setLoading(false);
        return;
      }
      if (
        isMonteur &&
        zzpMode &&
        !String(selectedProjectClientId || clientId || "").trim()
      ) {
        setError("Kies klant of project");
        setLoading(false);
        return;
      }

      const effectiveAssignedTo = isMonteur ? currentUserId : assignedTo;
      if (wantsDirectPlanning && (!String(effectiveAssignedTo || "").trim() || !String(plannedDate || "").trim())) {
        setError("Voor direct inplannen zijn monteur en geplande datum verplicht.");
        setLoading(false);
        return;
      }

      const plannedMinutes = parsePositiveWholeMinutes(plannedMinutesInput);
      if (!plannedMinutes) {
        setError("Geplande tijd moet een positief geheel aantal minuten zijn.");
        setLoading(false);
        return;
      }
      const selectedTypeOption =
        workorderTypeOptions.find((option) => option.value === type) || LEGACY_TYPE_OPTIONS[0];

      const payload = {
        clientId: selectedProjectClientId || clientId,
        projectId: projectId || undefined,
        werkbonNummer,
        type: mapWorkorderTypeToInternalType(selectedTypeOption),
        workorderType: String(selectedTypeOption?.workorderType || type || "SERVICE").toUpperCase(),
        omschrijving: omschrijving.trim(),
        locationText: locatie.trim(),
        opdracht: {
          omschrijving: omschrijving.trim(),
          locatie: locatie.trim(),
        },
        scopeSamenvatting: omschrijving.trim(),
        assignedTo: wantsDirectPlanning ? effectiveAssignedTo || undefined : undefined,
        plannedDate: wantsDirectPlanning ? plannedDate || undefined : undefined,
        plannedMinutes,
        createAndReady: submitMode === "workvoorraad" || wantsDirectPlanning,
        timeMode: "manual",
        contact: selectedContact
          ? {
              contactPersonId: selectedContact._id,
              name: selectedContact.name || "",
              role: selectedContact.role || "",
              phone: selectedContact.phone || "",
              email: selectedContact.email || "",
              unknown: false,
            }
          : {
              unknown: true,
            },
        assetLogbook: {
          enabled: assetLogbookEnabled,
          assetId: assetId || null,
          category: assetLogbookEnabled ? assetLogbookCategory || defaultAssetLogbookCategory(selectedTypeOption) : "",
          customerVisible: assetLogbookEnabled ? assetLogbookCustomerVisible === true : false,
          createEntryOn: "completion",
        },
        logbookInstallationId: assetLogbookEnabled ? installationId || undefined : undefined,
      };

      const res = await api.post("/werkbonnen", payload);
      const createdWerkbon = res?.data;

      if (createdWerkbon?._id && bijlageFiles.length > 0) {
        const uploadResult = await uploadWorkorderAttachments(createdWerkbon._id, bijlageFiles);
        if (uploadResult.failed.length > 0) {
          const firstFailure = uploadResult.failed[0];
          window.alert(
            `Werkbon is aangemaakt, maar ${uploadResult.failed.length} bijlage(n) konden niet worden geüpload. Eerste fout: ${firstFailure.message}`
          );
        }
      }

      onCreated?.(createdWerkbon);
      onClose();
    } catch (err) {
      console.error("Werkbon aanmaken mislukt:", err);
      const nextFieldErrors = getApiFieldErrors(err);
      setFieldErrors(nextFieldErrors);
      if (hasApiErrorCode(err, "VALIDATION_ERROR")) {
        setError("Controleer de invoer en probeer opnieuw.");
      } else {
        const normalized = showApiErrorToast(err, { fallbackMessage: "Werkbon aanmaken mislukt" });
        setError(normalized.message || getApiErrorMessage(err, "Werkbon aanmaken mislukt"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function refreshClientsAfterCreate(createdClient = null) {
    setShowNewClient(false);

    try {
      let nextClients = [];
      if (isMonteur) {
        const res = await api.get("/werkbonnen/create-options");
        nextClients = Array.isArray(res?.data?.clients) ? res.data.clients : [];
      } else {
        const res = await api.get("/clients");
        nextClients = Array.isArray(res.data) ? res.data : [];
      }
      setClients(nextClients);

      const createdId = String(createdClient?._id || "").trim();
      if (createdId && nextClients.some((item) => String(item?._id || "") === createdId)) {
        setClientId(createdId);
        return;
      }

      const last = nextClients[nextClients.length - 1];
      if (last?._id) {
        setClientId(String(last._id));
      }
    } catch (err) {
      console.error("Clients refresh fout:", err);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b bg-gray-50 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold">Nieuwe werkbon</h2>
            <p className="text-sm text-gray-500">
              Pilot flow: minimale invoer, snelle planning, direct klaar voor uitvoering.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-xl text-gray-500 hover:text-black"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
          {Object.keys(fieldErrors).length > 0 && (
            <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <ul className="list-disc space-y-1 pl-4">
                {Object.entries(fieldErrors).map(([field, messages]) => (
                  <li key={field}>{Array.isArray(messages) ? messages[0] : String(messages || "")}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <section className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-3 font-semibold text-blue-700">1. Basis</h3>

              <div className="grid gap-4 md:grid-cols-2">
                {(!isMonteur || zzpMode) && (
                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium">Klant *</label>
                  <div className="flex gap-2">
                    <select
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      required
                      className="flex-1 rounded border p-2"
                    >
                      <option value="">-- kies klant --</option>
                      {clients.map((client) => (
                        <option key={client._id} value={client._id}>
                          {client.naam}
                        </option>
                      ))}
                    </select>

                    {(!isMonteur || zzpMode) && (
                      <button
                        type="button"
                        onClick={() => setShowNewClient(true)}
                        className="rounded bg-blue-600 px-3 py-2 text-white hover:bg-blue-700"
                        title="Nieuwe klant"
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium">Type *</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full rounded border p-2"
                  >
                    {workorderTypeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {selectedTypeOptionForForm?.requireHours === false
                      ? "Urenregistratie niet verplicht"
                      : "Urenregistratie verplicht"}
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Project {isMonteur && !zzpMode ? "*" : ""}
                  </label>
                  <select
                    value={projectId}
                    onChange={(e) => {
                      setProjectId(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, projectId: undefined }));
                    }}
                    className="w-full rounded border p-2"
                    disabled={!isMonteur && !clientId}
                    required={isMonteur && !zzpMode}
                  >
                    <option value="">
                      {isMonteur && !zzpMode
                        ? "-- kies bestaand project --"
                        : isMonteur && zzpMode
                          ? "-- kies project (optioneel) --"
                          : clientId
                            ? "-- kies project (optioneel) --"
                            : "-- kies eerst klant --"}
                    </option>
                    {projects
                      .filter((project) => {
                        const projectClientId = String(project?.clientId?._id || project?.clientId || "");
                        if (isMonteur && zzpMode && clientId) {
                          return projectClientId === String(clientId);
                        }
                        if (isMonteur) return true;
                        return projectClientId === String(clientId);
                      })
                      .map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.projectNummer || project.number
                            ? `${project.projectNummer || project.number} - ${project.naam || project.title || "Project"}`
                            : project.naam || project.title || "Project"}
                        </option>
                      ))}
                  </select>
                  {fieldErrors?.projectId?.[0] && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.projectId[0]}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">Werkbonnummer</label>
                  <input
                    value={werkbonNummer}
                    readOnly
                    className="w-full rounded border bg-gray-100 p-2 text-gray-600"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium">Locatie *</label>
                  <input
                    value={locatie}
                    onChange={(e) => setLocatie(e.target.value)}
                    required
                    placeholder="Bijv. Lounge 2 Schiphol Hermes"
                    className="w-full rounded border p-2"
                  />
                  {selectedProject?.locationText && (
                    <p className="mt-1 text-xs text-gray-500">{locationSourceLabel}.</p>
                  )}
                </div>

                <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                    <input
                      type="checkbox"
                      checked={assetLogbookEnabled}
                      onChange={(event) => {
                        setAssetLogbookTouched(true);
                        setAssetLogbookEnabled(event.target.checked);
                      }}
                    />
                    Toevoegen aan assetlogboek
                  </label>

                  {assetLogbookEnabled ? (
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                          Installatie
                        </label>
                        <select
                          value={installationId}
                          onChange={(event) => setInstallationId(event.target.value)}
                          className="w-full rounded border p-2 text-sm"
                        >
                          <option value="">-- kies installatie (optioneel) --</option>
                          {filteredInstallations.map((item) => (
                            <option key={item._id} value={item._id}>
                              {(item.naam || "Installatie")} {item.locatie ? `- ${item.locatie}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                          Asset
                        </label>
                        <select
                          value={assetId}
                          onChange={(event) => setAssetId(event.target.value)}
                          className="w-full rounded border p-2 text-sm"
                          disabled={!installationId || installationAssets.length === 0}
                        >
                          <option value="">-- kies asset (optioneel) --</option>
                          {installationAssets.map((item) => (
                            <option key={item._id} value={item._id}>
                              {item.naam || item.type || "Asset"}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
                          Categorie
                        </label>
                        <input
                          value={assetLogbookCategory}
                          onChange={(event) => setAssetLogbookCategory(event.target.value)}
                          className="w-full rounded border p-2 text-sm"
                          placeholder="onderhoud, storing, inspectie, keuring, herstel"
                        />
                      </div>

                      <label className="mt-5 flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={assetLogbookCustomerVisible}
                          onChange={(event) => setAssetLogbookCustomerVisible(event.target.checked)}
                        />
                        Klantzichtbaar
                      </label>
                    </div>
                  ) : null}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium">Werkopdracht *</label>
                  <textarea
                    value={omschrijving}
                    onChange={(e) => {
                      setOmschrijving(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, omschrijving: undefined }));
                    }}
                    required
                    rows={5}
                    placeholder="Beschrijf kort en concreet wat de monteur moet doen."
                    className="w-full rounded border p-2"
                  />
                  {fieldErrors?.omschrijving?.[0] && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.omschrijving[0]}</p>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-lg border bg-white p-4">
              <h3 className="mb-3 font-semibold text-blue-700">2. Planning</h3>

              <div className="mb-4 grid gap-3 md:grid-cols-3">
                {SUBMIT_MODE_OPTIONS.map((option) => {
                  const active = submitMode === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSubmitMode(option.value)}
                      className={`rounded-xl border px-4 py-3 text-left transition ${
                        active
                          ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <p className="text-sm font-semibold">{option.label}</p>
                      <p className={`mt-1 text-xs ${active ? "text-slate-200" : "text-slate-500"}`}>
                        {option.description}
                      </p>
                    </button>
                  );
                })}
              </div>

              {selectedContact && (
                <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>
                    Contact: {[selectedContact.name, selectedContact.role, selectedContact.phone]
                      .filter(Boolean)
                      .join(" - ")}
                  </span>
                  {contactSourceLabel && (
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                      {contactSourceLabel}
                    </span>
                  )}
                </div>
              )}

              <div className={`grid gap-4 md:grid-cols-2 ${submitMode === "concept" ? "opacity-60" : ""}`}>
                <div>
                  <label className="mb-1 block text-sm font-medium">Toegewezen monteur</label>
                  <select
                    value={assignedTo}
                    onChange={(e) => {
                      setAssignedTo(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, assignedTo: undefined }));
                    }}
                    className="w-full rounded border p-2"
                  >
                    <option value="">-- kies monteur --</option>
                    {monteurs.map((monteur) => (
                      <option key={monteur._id} value={monteur._id}>
                        {monteur.naam || monteur.email || monteur._id}
                      </option>
                    ))}
                  </select>
                  {fieldErrors?.assignedTo?.[0] && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.assignedTo[0]}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">Geplande datum</label>
                  <input
                    type="date"
                    value={plannedDate}
                    onChange={(e) => {
                      setPlannedDate(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, plannedDate: undefined }));
                    }}
                    className="w-full rounded border p-2"
                  />
                  {fieldErrors?.plannedDate?.[0] && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.plannedDate[0]}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium">Geplande tijd</label>
                  <div className="mb-2 flex flex-wrap gap-2">
                    {QUICK_PLANNED_MINUTES.map((minutes) => (
                      <button
                        key={minutes}
                        type="button"
                        onClick={() => setPlannedMinutesInput(String(minutes))}
                        className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-700 hover:border-slate-400"
                      >
                        {minutes} min
                      </button>
                    ))}
                  </div>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={plannedMinutesInput}
                    onChange={(e) => {
                      setPlannedMinutesInput(e.target.value);
                      setFieldErrors((prev) => ({
                        ...prev,
                        plannedMinutes: undefined,
                        plannedDurationMinutes: undefined,
                      }));
                    }}
                    className="w-full rounded border p-2"
                    placeholder="Aantal minuten"
                  />
                  {(fieldErrors?.plannedMinutes?.[0] || fieldErrors?.plannedDurationMinutes?.[0]) && (
                    <p className="mt-1 text-xs text-red-600">
                      {fieldErrors?.plannedMinutes?.[0] || fieldErrors?.plannedDurationMinutes?.[0]}
                    </p>
                  )}
                </div>
              </div>

              <p className="mt-3 text-xs text-gray-500">
                {submitMode === "concept"
                  ? "Concept bewaart alleen de basis. Werkbon komt nog niet in planning of werkvoorraad."
                  : submitMode === "workvoorraad"
                    ? "Werkvoorraad zet de werkbon op gereed voor planning. Datum en monteur zijn nog optioneel."
                    : "Direct inplannen zet de werkbon direct gepland. Datum en monteur zijn dan verplicht."}
              </p>
            </section>

            <section className="rounded-lg border bg-white p-4">
              <h3 className="mb-3 font-semibold text-blue-700">3. Documenten</h3>

              <label className="mb-1 block text-sm font-medium">Bijlage uploaden (optioneel)</label>
              <input
                type="file"
                multiple
                onChange={(e) => setBijlageFiles(Array.from(e.target.files || []))}
                className="w-full rounded border p-2"
              />
              <p className="mt-2 text-xs text-gray-500">
                Maximaal 25 MB per document. Bestanden worden na aanmaken direct geupload.
              </p>

              {bijlageFiles.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm text-gray-700">
                  {bijlageFiles.map((file) => (
                    <li key={`${file.name}-${file.size}`}>
                      - {file.name} ({formatMb(file.size)})
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <div className="flex justify-between border-t pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded border px-4 py-2 hover:bg-gray-100"
              >
                Annuleren
              </button>

              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="rounded bg-emerald-600 px-4 py-2 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {loading
                    ? bijlageFiles.length > 0
                      ? "Aanmaken + uploaden..."
                      : "Opslaan..."
                    : SUBMIT_BUTTON_LABELS[submitMode] || "Werkbon aanmaken"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showNewClient && (
        <NieuweKlantModal
          onClose={() => setShowNewClient(false)}
          onSaved={refreshClientsAfterCreate}
        />
      )}
    </div>
  );
}

function asArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  return [];
}

function mapWorkorderTypeToInternalType(option) {
  const normalized = String(option?.workorderType || option?.value || "").trim().toUpperCase();
  if (normalized.includes("OFFERTE")) return "OFFERTE";
  if (normalized.includes("PROJECT")) return "PROJECT";
  return "REGIE_2";
}

function getSuggestedContact(client, project) {
  const contacts = Array.isArray(client?.contactPersons) ? client.contactPersons : [];
  const projectContactId = String(project?.contactPersonId || "");

  if (projectContactId) {
    const projectContact = contacts.find(
      (contact) => String(contact?._id || "") === projectContactId && contact?.active !== false
    );

    if (projectContact) {
      return projectContact;
    }
  }

  return contacts.find((contact) => contact?.isDefault && contact?.active !== false)
    || contacts.find((contact) => contact?.active !== false)
    || null;
}

function resolveClientIdFromProject(project) {
  return String(project?.clientId?._id || project?.clientId || "").trim();
}

function getRoleNames(user) {
  const source = Array.isArray(user?.roles) ? user.roles : [];
  return source
    .map((role) => {
      if (!role) return "";
      if (typeof role === "string") return role;
      return role?.name || role?.naam || "";
    })
    .map((role) => String(role || "").trim().toLowerCase())
    .filter(Boolean);
}

function formatMb(size) {
  const mb = Number(size || 0) / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

function shouldDefaultAssetLogbookEnabled(option) {
  const key = String(option?.workorderType || option?.value || option?.label || "")
    .trim()
    .toLowerCase();
  return (
    key.includes("onderhoud") ||
    key.includes("storing") ||
    key.includes("inspectie") ||
    key.includes("keuring") ||
    key.includes("herstel") ||
    key.includes("maintenance") ||
    key.includes("incident") ||
    key.includes("inspection") ||
    key.includes("repair")
  );
}

function defaultAssetLogbookCategory(option) {
  const key = String(option?.workorderType || option?.value || option?.label || "")
    .trim()
    .toLowerCase();
  if (key.includes("storing") || key.includes("incident")) return "storing";
  if (key.includes("inspectie") || key.includes("inspection")) return "inspectie";
  if (key.includes("keuring")) return "keuring";
  if (key.includes("herstel") || key.includes("repair")) return "herstel";
  if (key.includes("onderhoud") || key.includes("maintenance")) return "onderhoud";
  return "overig";
}

const QUICK_PLANNED_MINUTES = [30, 60, 120, 240];

const SUBMIT_MODE_OPTIONS = [
  {
    value: "concept",
    label: "Concept bewaren",
    description: "Alleen basisgegevens opslaan. Nog niet planbaar.",
  },
  {
    value: "workvoorraad",
    label: "Naar werkvoorraad",
    description: "Klaarzetten voor planner zonder datum of monteur.",
  },
  {
    value: "direct",
    label: "Direct inplannen",
    description: "Meteen op datum en monteur zetten.",
  },
];

const SUBMIT_BUTTON_LABELS = {
  concept: "Concept opslaan",
  workvoorraad: "Opslaan naar werkvoorraad",
  direct: "Direct inplannen",
};

const LEGACY_TYPE_OPTIONS = [
  { value: "service", label: "Service", workorderType: "SERVICE", requireHours: true },
  { value: "regie", label: "Regie", workorderType: "REGIE", requireHours: true },
  { value: "offerte", label: "Offerte", workorderType: "OFFERTE", requireHours: true },
];

function normalizeWorkorderTypeOptions(value) {
  const items = Array.isArray(value) ? value : [];
  const next = items
    .filter((item) => item?.active !== false)
    .map((item) => {
      const rawKey = String(item?.key || item?.systemKey || "").trim();
      if (!rawKey) return null;
      const label = String(item?.name || rawKey).trim();
      const valueKey = rawKey.toLowerCase();
      return {
        value: valueKey,
        label,
        workorderType: rawKey.toUpperCase(),
        requireHours: deriveWorkorderTypeRequireHours(item),
        sortOrder: Number.isFinite(Number(item?.sortOrder)) ? Number(item.sortOrder) : 9999,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.label.localeCompare(b.label, "nl");
    });

  const deduped = [];
  const seen = new Set();
  for (const option of next) {
    if (seen.has(option.value)) continue;
    seen.add(option.value);
    deduped.push({
      value: option.value,
      label: option.label,
      workorderType: option.workorderType,
      requireHours: option.requireHours !== false,
    });
  }
  return deduped;
}

function deriveWorkorderTypeRequireHours(item) {
  const workflowRequireHours =
    item?.workflow && typeof item.workflow === "object"
      ? item.workflow.requireHours
      : undefined;
  if (workflowRequireHours === undefined) {
    return item?.allowNacalculatie !== false;
  }
  return Boolean(workflowRequireHours);
}
